/**
 * Job Applications Tab Component
 * Displays and manages job applications from the public application form
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  UserPlus,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Award,
  Car,
  Shield,
  Calendar,
  Trash2,
  RefreshCw,
  UserCheck,
  ArrowRight,
  Printer,
  Download,
  Snowflake,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-blue-100 text-blue-700", icon: Clock },
  reviewed: { label: "Reviewed", color: "bg-purple-100 text-purple-700", icon: Eye },
  contacted: { label: "Contacted", color: "bg-amber-100 text-amber-700", icon: Phone },
  interviewed: { label: "Interviewed", color: "bg-cyan-100 text-cyan-700", icon: Briefcase },
  hired: { label: "Hired", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  cold: { label: "Cold", color: "bg-sky-100 text-sky-700", icon: Snowflake },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

const positionLabels = {
  pcw: "Personal Care Worker",
  cna: "Certified Nursing Assistant",
  hha: "Home Health Aide",
  rn: "Registered Nurse",
  lpn: "Licensed Practical Nurse",
  case_manager: "Case Manager",
  receptionist: "Receptionist",
  other: "Other",
};

export function JobApplicationsTab({ applications, loading, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [employeeData, setEmployeeData] = useState({
    hire_date: new Date().toISOString().split('T')[0],
    role: "pcw",
    employment_type: "full_time",
    pay_rate: "",
    pay_type: "hourly",
    assessment_visit_rate: "",
    supervisory_visit_rate: "",
    documentation_incentive_rate: "",
  });

  const filteredApplications = applications.filter((app) => {
    const matchesSearch = 
      `${app.first_name} ${app.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.position_applied?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleUpdateStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/public/job-application/${applicationId}/status?status=${newStatus}`);
      toast.success(`Application status updated to ${statusConfig[newStatus]?.label}`);
      onRefresh();
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? `Failed to update status: ${detail}`
          : "Failed to update status"
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConvertToEmployee = async () => {
    if (!selectedApplication) return;
    
    setConverting(true);
    try {
      // Generate a temporary password
      const tempPassword = `Welcome${Math.random().toString(36).slice(-6)}!`;
      
      // Create employee from application data
      const newEmployee = {
        name: `${selectedApplication.first_name} ${selectedApplication.last_name}`,
        email: selectedApplication.email,
        password: tempPassword,
        phone: selectedApplication.phone || "",
        role: employeeData.role,
        address: selectedApplication.address || "",
        city: selectedApplication.city || "",
        state: selectedApplication.state || "",
        zip_code: selectedApplication.zip_code || "",
        hire_date: employeeData.hire_date,
        employment_type: employeeData.employment_type,
        pay_rate: parseFloat(employeeData.pay_rate) || 0,
        pay_type: employeeData.pay_type,
        certifications: selectedApplication.certifications?.join(", ") || "",
        notes: `Converted from job application on ${new Date().toLocaleDateString()}. Original application date: ${formatDate(selectedApplication.submitted_at)}`,
      };
      // RNs can carry two additional per-visit rates on top of their base pay
      if (employeeData.role === "rn") {
        const asmt = parseFloat(employeeData.assessment_visit_rate);
        const sup = parseFloat(employeeData.supervisory_visit_rate);
        if (!Number.isNaN(asmt) && asmt > 0) newEmployee.assessment_visit_rate = asmt;
        if (!Number.isNaN(sup) && sup > 0) newEmployee.supervisory_visit_rate = sup;
      }
      // Optional documentation-completeness / shift-differential incentive
      const incentive = parseFloat(employeeData.documentation_incentive_rate);
      if (!Number.isNaN(incentive) && incentive > 0) {
        newEmployee.documentation_incentive_rate = incentive;
      }

      await axios.post(`${API}/employees`, newEmployee);
      
      // Update application status to indicate conversion
      await axios.put(`${API}/public/job-application/${selectedApplication.id}/status?status=hired`);
      
      toast.success(`${selectedApplication.first_name} ${selectedApplication.last_name} has been added as an active employee! Temp password: ${tempPassword}`);
      setConvertDialogOpen(false);
      setDetailsOpen(false);
      onRefresh();
      
      // Reset employee data form
      setEmployeeData({
        hire_date: new Date().toISOString().split('T')[0],
        role: "pcw",
        employment_type: "full_time",
        pay_rate: "",
        pay_type: "hourly",
        assessment_visit_rate: "",
        supervisory_visit_rate: "",
        documentation_incentive_rate: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert to employee");
    } finally {
      setConverting(false);
    }
  };

  const openConvertDialog = (app) => {
    setSelectedApplication(app);
    // Pre-fill role based on position applied
    const roleMap = {
      pcw: "pcw",
      cna: "pcw",
      hha: "pcw",
      rn: "rn",
      lpn: "rn",
      case_manager: "case_manager",
      receptionist: "receptionist",
    };
    setEmployeeData(prev => ({
      ...prev,
      role: roleMap[app.position_applied] || "pcw",
    }));
    setConvertDialogOpen(true);
  };

  const handleDelete = async (applicationId) => {
    if (!confirm("Are you sure you want to delete this application?")) return;
    
    try {
      await axios.delete(`${API}/public/job-application/${applicationId}`);
      toast.success("Application deleted");
      onRefresh();
      setDetailsOpen(false);
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  // Fetch the PDF as a blob so we can (a) open it in a new tab for print or
  // (b) download it to disk. Using blobs avoids a naked-URL open that would
  // send stale cookies / auth headers and lets us name the downloaded file.
  const fetchApplicationPdfBlob = async (applicationId) => {
    const res = await axios.get(
      `${API}/public/job-application/${applicationId}/pdf`,
      { responseType: "blob" }
    );
    return res.data;
  };

  const handlePrintApplication = async (application) => {
    try {
      const blob = await fetchApplicationPdfBlob(application.id);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Popup blocked — allow popups to print the application.");
        URL.revokeObjectURL(url);
        return;
      }
      // Give the browser a moment to load the PDF viewer before printing.
      const attemptPrint = () => {
        try {
          win.focus();
          win.print();
        } catch (_) { /* browser will still show the PDF viewer */ }
      };
      win.addEventListener("load", attemptPrint);
      setTimeout(attemptPrint, 900);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not print application");
    }
  };

  const handleDownloadApplication = async (application) => {
    try {
      const blob = await fetchApplicationPdfBlob(application.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `application_${application.last_name || "applicant"}_${application.first_name || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not download application");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Job Applications
          </h2>
          <p className="text-sm text-slate-500">
            Review and manage applications from your public careers page
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const count = applications.filter((a) => a.status === status).length;
          const Icon = config.icon;
          return (
            <div
              key={status}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                statusFilter === status ? "ring-2 ring-primary" : ""
              } ${config.color.replace("text-", "border-").split(" ")[0]}`}
              onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <span className="text-2xl font-bold">{count}</span>
              </div>
              <p className="text-xs mt-1">{config.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statusConfig).map(([value, config]) => (
              <SelectItem key={value} value={value}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-16">
            <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No applications found</p>
            <p className="text-sm text-slate-400 mt-1">
              {applications.length === 0
                ? "Applications will appear here when candidates apply through your public careers link"
                : "Try adjusting your search or filters"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Applicant</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Experience</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApplications.map((app) => {
                const StatusIcon = statusConfig[app.status]?.icon || Clock;
                return (
                  <TableRow
                    key={app.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      setSelectedApplication(app);
                      setDetailsOpen(true);
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                          {app.first_name?.[0]}{app.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium">{app.first_name} {app.last_name}</p>
                          <p className="text-xs text-slate-500">{app.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-slate-100 rounded text-sm">
                        {positionLabels[app.position_applied] || app.position_applied}
                      </span>
                    </TableCell>
                    <TableCell>
                      {app.experience_years || 0} years
                    </TableCell>
                    <TableCell>
                      {formatDate(app.submitted_at)}
                    </TableCell>
                    <TableCell>
                      <span className={`badge ${statusConfig[app.status]?.color || "bg-slate-100"}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig[app.status]?.label || app.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(app.status === "hired" || app.status === "interviewed") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConvertDialog(app);
                            }}
                            title="Convert to Employee"
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintApplication(app);
                          }}
                          title="Print application"
                          data-testid={`print-application-${app.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApplication(app);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Application Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedApplication && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  {selectedApplication.first_name} {selectedApplication.last_name}
                </DialogTitle>
                <DialogDescription>
                  Applied for {positionLabels[selectedApplication.position_applied] || selectedApplication.position_applied} on {formatDate(selectedApplication.submitted_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status Update */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Update Application Status
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusConfig).map(([status, config]) => {
                      const Icon = config.icon;
                      return (
                        <Button
                          key={status}
                          variant={selectedApplication.status === status ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedApplication.id, status)}
                          disabled={updatingStatus}
                          className="gap-1"
                        >
                          <Icon className="w-3 h-3" />
                          {config.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-blue-600">Email</p>
                      <a href={`mailto:${selectedApplication.email}`} className="text-sm font-medium text-blue-800 hover:underline">
                        {selectedApplication.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="text-xs text-emerald-600">Phone</p>
                      <a href={`tel:${selectedApplication.phone}`} className="text-sm font-medium text-emerald-800 hover:underline">
                        {selectedApplication.phone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address */}
                {selectedApplication.address && (
                  <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="w-4 h-4 text-slate-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-600">Address</p>
                      <p className="text-sm font-medium">
                        {selectedApplication.address}
                        {selectedApplication.city && `, ${selectedApplication.city}`}
                        {selectedApplication.state && `, ${selectedApplication.state}`}
                        {selectedApplication.zip_code && ` ${selectedApplication.zip_code}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Position Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-slate-600" />
                      <p className="text-xs text-slate-600">Position Applied</p>
                    </div>
                    <p className="font-medium">
                      {positionLabels[selectedApplication.position_applied] || selectedApplication.position_applied}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-slate-600" />
                      <p className="text-xs text-slate-600">Availability</p>
                    </div>
                    <p className="font-medium capitalize">
                      {selectedApplication.availability?.replace("_", " ") || "Not specified"}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Award className="w-4 h-4 text-slate-600" />
                      <p className="text-xs text-slate-600">Experience</p>
                    </div>
                    <p className="font-medium">{selectedApplication.experience_years || 0} years</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-slate-600" />
                      <p className="text-xs text-slate-600">Start Date</p>
                    </div>
                    <p className="font-medium">{formatDate(selectedApplication.start_date) || "Flexible"}</p>
                  </div>
                </div>

                {/* Certifications */}
                {selectedApplication.certifications?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-medium text-amber-800">Certifications</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedApplication.certifications.map((cert, i) => (
                        <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Requirements */}
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-3 rounded-lg ${selectedApplication.has_drivers_license ? "bg-emerald-50" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2">
                      <Car className={`w-4 h-4 ${selectedApplication.has_drivers_license ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="text-xs">Drivers License</span>
                    </div>
                    <p className={`font-medium ${selectedApplication.has_drivers_license ? "text-emerald-700" : "text-slate-500"}`}>
                      {selectedApplication.has_drivers_license ? "Yes" : "No"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${selectedApplication.has_reliable_transportation ? "bg-emerald-50" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2">
                      <Car className={`w-4 h-4 ${selectedApplication.has_reliable_transportation ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="text-xs">Transportation</span>
                    </div>
                    <p className={`font-medium ${selectedApplication.has_reliable_transportation ? "text-emerald-700" : "text-slate-500"}`}>
                      {selectedApplication.has_reliable_transportation ? "Yes" : "No"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${selectedApplication.can_pass_background_check ? "bg-emerald-50" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${selectedApplication.can_pass_background_check ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="text-xs">Background Check</span>
                    </div>
                    <p className={`font-medium ${selectedApplication.can_pass_background_check ? "text-emerald-700" : "text-slate-500"}`}>
                      {selectedApplication.can_pass_background_check ? "Yes" : "No"}
                    </p>
                  </div>
                </div>

                {/* References */}
                {selectedApplication.references && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-1">References</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedApplication.references}</p>
                  </div>
                )}

                {/* Additional Info */}
                {selectedApplication.additional_info && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-1">Additional Information</p>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedApplication.additional_info}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(selectedApplication.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintApplication(selectedApplication)}
                    data-testid="print-application-btn"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadApplication(selectedApplication)}
                    data-testid="download-application-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download PDF
                  </Button>
                  {(selectedApplication.status === "hired" || selectedApplication.status === "interviewed") && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                      onClick={() => {
                        setDetailsOpen(false);
                        openConvertDialog(selectedApplication);
                      }}
                    >
                      <UserCheck className="w-4 h-4" />
                      Convert to Employee
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                    Close
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Employee Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Convert to Active Employee
            </DialogTitle>
            <DialogDescription>
              Add {selectedApplication?.first_name} {selectedApplication?.last_name} to your employee roster
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Applicant Summary */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-lg">
                  {selectedApplication?.first_name?.[0]}{selectedApplication?.last_name?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    {selectedApplication?.first_name} {selectedApplication?.last_name}
                  </p>
                  <p className="text-sm text-slate-500">{selectedApplication?.email}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Applied: {formatDate(selectedApplication?.submitted_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select 
                  value={employeeData.role} 
                  onValueChange={(v) => setEmployeeData(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcw">Personal Care Worker</SelectItem>
                    <SelectItem value="rn">Registered Nurse</SelectItem>
                    <SelectItem value="case_manager">Case Manager</SelectItem>
                    <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                    <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                    <SelectItem value="intake_coordinator">Intake Coordinator</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Hire Date *</Label>
                <Input
                  type="date"
                  value={employeeData.hire_date}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, hire_date: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select 
                  value={employeeData.employment_type} 
                  onValueChange={(v) => setEmployeeData(prev => ({ ...prev, employment_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-Time</SelectItem>
                    <SelectItem value="part_time">Part-Time</SelectItem>
                    <SelectItem value="per_diem">Per Diem</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Pay Type</Label>
                <Select 
                  value={employeeData.pay_type} 
                  onValueChange={(v) => setEmployeeData(prev => ({ ...prev, pay_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pay type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 col-span-2">
                <Label>Pay Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={employeeData.pay_type === "hourly" ? "e.g., 15.00 per hour" : "e.g., 45000 per year"}
                  value={employeeData.pay_rate}
                  onChange={(e) => setEmployeeData(prev => ({ ...prev, pay_rate: e.target.value }))}
                />
              </div>

              {/* RN-only per-visit rates (Assessment / Supervisory) */}
              {employeeData.role === "rn" && (
                <div className="col-span-2 space-y-3 p-3 rounded-lg border border-sky-200 bg-sky-50/60">
                  <p className="text-xs font-medium text-sky-800">
                    Per-Visit Rates (Registered Nurses)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Assessment Visit Rate ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="e.g., 75.00 per assessment"
                        value={employeeData.assessment_visit_rate}
                        onChange={(e) => setEmployeeData(prev => ({ ...prev, assessment_visit_rate: e.target.value }))}
                        data-testid="assessment-visit-rate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Supervisory Visit Rate ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="e.g., 50.00 per supervisory"
                        value={employeeData.supervisory_visit_rate}
                        onChange={(e) => setEmployeeData(prev => ({ ...prev, supervisory_visit_rate: e.target.value }))}
                        data-testid="supervisory-visit-rate-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-sky-700">
                    These pay in addition to the base pay rate above and only
                    apply on Assessment / Supervisory shifts.
                  </p>
                </div>
              )}

              {/* Documentation / shift-differential incentive — applies to any role */}
              <div className="col-span-2 space-y-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/60">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm text-emerald-900">
                    Documentation Incentive / Shift Differential ($/hour)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-40"
                    placeholder="e.g., 1.50"
                    value={employeeData.documentation_incentive_rate}
                    onChange={(e) => setEmployeeData(prev => ({ ...prev, documentation_incentive_rate: e.target.value }))}
                    data-testid="documentation-incentive-rate-input"
                  />
                </div>
                <p className="text-xs text-emerald-800">
                  Optional extra hourly rate paid when the caregiver's shift
                  documentation is accurate + complete on time. You'll get a
                  checkbox to award it when verifying each visit.
                </p>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>What happens next:</strong> The applicant will be added to your employee roster 
                as an active employee. Their contact info, certifications, and other details will be transferred.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              onClick={handleConvertToEmployee}
              disabled={converting}
            >
              {converting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" />
                  Add as Employee
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
